import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Space, Button } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ExperimentOutlined,
  DatabaseOutlined,
  MessageOutlined,
  FileTextOutlined,
  SettingOutlined,
  UserOutlined,
  CloudUploadOutlined,
  QuestionCircleOutlined,
  KeyOutlined,
  SearchOutlined,
  MonitorOutlined
} from '@ant-design/icons';
import { apiService } from '../../services/api';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStudy, setCurrentStudy] = useState<string | null>(null);

  // Check if we're in a study context and get study name
  useEffect(() => {
    const fetchStudyName = async () => {
      const path = location.pathname;
      const studyMatch = path.match(/\/(media-manager|question-manager|metadata-explorer|insight-console|insight-generator)\/([^\/]+)/);
      
      if (studyMatch) {
        const studyId = studyMatch[2];
        try {
          const study = await apiService.getStudy(studyId);
          setCurrentStudy(study.project_name || `Study ${studyId}`);
        } catch (error) {
          console.error('Failed to fetch study name:', error);
          setCurrentStudy(`Study ${studyId}`);
        }
      } else {
        setCurrentStudy(null);
      }
    };

    fetchStudyName();
  }, [location.pathname]);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/study-builder',
      icon: <ExperimentOutlined />,
      label: 'Study Builder',
    },
    {
      key: '/administration',
      icon: <SettingOutlined />,
      label: 'Administration',
    },
    {
      key: '/service-status',
      icon: <MonitorOutlined />,
      label: 'Service Status',
    },
    // Show study name and tools when a study is focused (but not on administration screens)
    ...(currentStudy && !location.pathname.includes('/administration') ? [
      {
        key: 'study-header',
        icon: <SearchOutlined />,
        label: currentStudy,
        disabled: true, // Make it non-clickable
        style: { 
          color: '#722ed1', 
          fontWeight: 'bold',
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e8e8e8'
        }
      },
      {
        key: `/question-manager/${location.pathname.split('/')[2] || 'new'}`,
        icon: <QuestionCircleOutlined />,
        label: 'Question Manager',
      },
      {
        key: `/media-manager/${location.pathname.split('/')[2] || 'new'}`,
        icon: <CloudUploadOutlined />,
        label: 'Media Manager',
      },
      {
        key: `/metadata-explorer/${location.pathname.split('/')[2] || 'new'}`,
        icon: <DatabaseOutlined />,
        label: 'Metadata Explorer',
      },
      {
        key: `/insight-console/${location.pathname.split('/')[2] || 'new'}`,
        icon: <MessageOutlined />,
        label: 'Insight Console',
      },
      {
        key: `/insight-generator/${location.pathname.split('/')[2] || 'new'}`,
        icon: <FileTextOutlined />,
        label: 'Insight Generator',
      },
    ] : []),
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  // Get current selected key based on path
  const getCurrentKey = () => {
    const path = location.pathname;
    if (path.includes('/question-manager')) return `/question-manager/${path.split('/')[2] || 'new'}`;
    if (path.includes('/media-manager')) return `/media-manager/${path.split('/')[2] || 'new'}`;
    if (path.includes('/metadata-explorer')) return `/metadata-explorer/${path.split('/')[2] || 'new'}`;
    if (path.includes('/insight-console')) return `/insight-console/${path.split('/')[2] || 'new'}`;
    if (path.includes('/insight-generator')) return `/insight-generator/${path.split('/')[2] || 'new'}`;
    if (path.includes('/study-builder')) return '/study-builder';
    if (path.includes('/administration')) return '/administration';
    if (path.includes('/service-status')) return '/service-status';
    return path;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Space align="center">
          <div style={{
            width: 32,
            height: 32,
            background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 16
          }}>
            R
          </div>
          <Title level={3} style={{ margin: 0, color: '#722ed1' }}>
            NRG Refract
          </Title>
        </Space>
        
        <Space>
          <Button 
            type="text" 
            icon={<UserOutlined />}
            style={{ border: '1px solid #d9d9d9' }}
          >
            Profile
          </Button>
        </Space>
      </Header>

      <Layout>
        <Sider 
          width={240} 
          style={{ 
            background: '#fff',
            borderRight: '1px solid #f0f0f0'
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[getCurrentKey()]}
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
            onClick={({ key }) => handleMenuClick(key)}
          />
        </Sider>

        <Layout>
          <Content style={{ 
            padding: '24px',
            background: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)',
            marginLeft: 0
          }}>
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout; 